import {
  type AtualizarPerfil,
  linkDeSenhaEmitidoSchema,
  paginaDeUsuariosSchema,
  sessaoAtualSchema,
  usuarioComConviteSchema,
  usuarioSchema,
  type AtualizarUsuario,
  type Credenciais,
  type CriarUsuario,
  type PaginaDeUsuarios,
  type RedefinirSenha,
  type SessaoAtual,
  type ConsultaDeUsuarios,
  type Usuario,
  type UsuarioComConvite,
} from "@base/contracts";
import { z } from "zod";
import type { ClienteHttp } from "../../../shared/http/cliente-http";
import { ErroDaApi } from "../../../shared/http/erro-da-api";

const semCorpo = z.null();

/**
 * Porta de saida do modulo no front. A apresentacao depende desta interface,
 * nao de `fetch`: o teste de componente usa o gateway em memoria.
 */
export interface UsuariosGateway {
  /** `null` quando nao ha sessao (401), em vez de erro: e o estado "deslogado". */
  sessaoAtual(): Promise<SessaoAtual | null>;
  entrar(credenciais: Credenciais): Promise<SessaoAtual>;
  sair(): Promise<void>;
  esqueciSenha(email: string): Promise<void>;
  redefinirSenha(dados: RedefinirSenha): Promise<void>;
  listar(consulta?: Pick<ConsultaDeUsuarios, "cursor" | "busca" | "papel" | "situacao">): Promise<PaginaDeUsuarios>;
  criar(dados: CriarUsuario): Promise<UsuarioComConvite>;
  atualizar(id: string, dados: AtualizarUsuario): Promise<Usuario>;
  enviarLinkDeSenha(id: string): Promise<{ linkDeSenha: string }>;
  excluir(id: string): Promise<void>;
  /** Dados da propria conta (nome, telefone, cargo): perfil e primeiro acesso. */
  salvarPerfil(dados: AtualizarPerfil): Promise<Usuario>;
}

/** Implementacao HTTP. Unico arquivo do front que conhece os caminhos destas rotas. */
export class UsuariosGatewayHttp implements UsuariosGateway {
  constructor(private readonly http: ClienteHttp) {}

  async sessaoAtual(): Promise<SessaoAtual | null> {
    try {
      return await this.http.requisitar("/v1/sessoes/atual", sessaoAtualSchema);
    } catch (erro) {
      if (erro instanceof ErroDaApi && erro.ehNaoAutorizado) return null;
      throw erro;
    }
  }

  entrar(credenciais: Credenciais): Promise<SessaoAtual> {
    return this.http.requisitar("/v1/sessoes", sessaoAtualSchema, {
      metodo: "POST",
      corpo: credenciais,
    });
  }

  async sair(): Promise<void> {
    await this.http.requisitar("/v1/sessoes/atual", semCorpo, { metodo: "DELETE" });
  }

  async esqueciSenha(email: string): Promise<void> {
    await this.http.requisitar("/v1/senhas/esqueci", semCorpo, {
      metodo: "POST",
      corpo: { email },
    });
  }

  async redefinirSenha(dados: RedefinirSenha): Promise<void> {
    await this.http.requisitar("/v1/senhas/redefinir", semCorpo, {
      metodo: "POST",
      corpo: dados,
    });
  }

  listar(consulta: Pick<ConsultaDeUsuarios, "cursor" | "busca" | "papel" | "situacao"> = {}): Promise<PaginaDeUsuarios> {
    return this.http.requisitar("/v1/usuarios", paginaDeUsuariosSchema, {
      consulta: {
        cursor: consulta.cursor,
        busca: consulta.busca,
        papel: consulta.papel,
        situacao: consulta.situacao,
      },
    });
  }

  criar(dados: CriarUsuario): Promise<UsuarioComConvite> {
    return this.http.requisitar("/v1/usuarios", usuarioComConviteSchema, { metodo: "POST", corpo: dados });
  }

  atualizar(id: string, dados: AtualizarUsuario): Promise<Usuario> {
    return this.http.requisitar(`/v1/usuarios/${encodeURIComponent(id)}`, usuarioSchema, {
      metodo: "PATCH",
      corpo: dados,
    });
  }

  salvarPerfil(dados: AtualizarPerfil): Promise<Usuario> {
    return this.http.requisitar("/v1/perfil", usuarioSchema, { metodo: "PUT", corpo: dados });
  }

  async excluir(id: string): Promise<void> {
    await this.http.requisitar(`/v1/usuarios/${encodeURIComponent(id)}`, semCorpo, {
      metodo: "DELETE",
    });
  }

  enviarLinkDeSenha(id: string): Promise<{ linkDeSenha: string }> {
    return this.http.requisitar(
      `/v1/usuarios/${encodeURIComponent(id)}/link-de-senha`,
      linkDeSenhaEmitidoSchema,
      { metodo: "POST" },
    );
  }
}
